import axios from "axios";
import { getBackendUrl } from "@Utils";
import { objectIdString } from "@Helpers/schemas/common.schema";
import type {
  BrowseSharedFolderRequest,
  GetSharedFileAccessRequest,
  GetSharedFileAccessResponse,
  GetSharedFileVersionDataRequest,
  GetSharedFileVersionDataResponse,
  GetSharedModelAssetsRequest,
  GetSharedOfficeUrlResponse,
  GetSharedModelAssetsResponse,
  BrowseSharedFolderResponse,
  GetSharedFileRequest,
  GetSharedFileResponse,
  RequestSharedDownloadRequest,
  RequestSharedDownloadResponse,
  ResolveSharedLinkResponse,
  SharedBreadcrumb,
  SharedFileVersion,
  SharedFolderEntry,
  SharedLinkInfo,
  UnlockSharedLinkResponse,
} from "@ServerTypes/externalLinks/externalLinks.schema";

/**
 * API client for the RECIPIENT-facing share routes.
 *
 * Deliberately a bare `axios` instance rather than the app's `axiosWrapper`. The wrapper is built
 * for an authenticated session: it attaches the JWT, participates in CSRF, and on a 401 clears
 * local storage and redirects to the sign-in page. None of that is right for a visitor who has no
 * CUBE account — in particular the 401 redirect would replace the share page with a login screen.
 *
 * `withCredentials: false` also guarantees a signed-in employee opening a share link in their own
 * browser does not have their session cookie ride along to these endpoints; the token in the URL is
 * the only credential these routes are meant to see.
 */
const shareClient = axios.create({ withCredentials: false });

const base = () => `${getBackendUrl()}/externalLinks`;

export type { SharedLinkInfo, SharedFolderEntry, SharedFileVersion, SharedBreadcrumb };

export async function resolveSharedLink(token: string, sessionToken?: string) {
  const res = await shareClient.post<ResolveSharedLinkResponse>(`${base()}/resolveSharedLink`, {
    token,
    sessionToken,
  });
  return res.data;
}

export async function unlockSharedLink(token: string, password: string) {
  const res = await shareClient.post<UnlockSharedLinkResponse>(`${base()}/unlockSharedLink`, {
    token,
    password,
  });
  return res.data;
}

/**
 * Params are the server's own request types throughout this file — including `skip`/`take`, which
 * exist because a shared folder can hold tens of thousands of files and neither the payload nor the
 * recipient's DOM should carry all of them at once.
 */
export async function browseSharedFolder(params: BrowseSharedFolderRequest) {
  const res = await shareClient.post<BrowseSharedFolderResponse>(`${base()}/browseSharedFolder`, params);
  return res.data;
}

export async function getSharedFile(params: GetSharedFileRequest) {
  const res = await shareClient.post<GetSharedFileResponse>(`${base()}/getSharedFile`, params);
  return res.data;
}

export async function requestSharedDownload(params: RequestSharedDownloadRequest) {
  const res = await shareClient.post<RequestSharedDownloadResponse>(`${base()}/requestSharedDownload`, params);
  return res.data;
}

/**
 * URL of the streaming route for a minted ticket.
 *
 * Whether the bytes render inline or save is decided when the ticket is minted and sealed inside it
 * — there is deliberately no flag here for the caller to flip.
 */
export function sharedDownloadUrl(ticket: string): string {
  return `${base()}/downloadSharedFile?ticket=${encodeURIComponent(ticket)}`;
}

/**
 * Trigger an ATTACHMENT download without navigating the page.
 *
 * A hidden iframe rather than `location.href`: the response is Content-Disposition attachment, so on
 * success the browser shows its save UI either way — but on an error (expired ticket, revoked link)
 * a navigation would replace the whole share page with the route's raw JSON, stranding the guest.
 * Inside the frame the error is invisible and the page stays intact.
 *
 * The frame is left in the DOM briefly and then removed; removing it immediately can abort the
 * download in some browsers before the save dialog takes ownership of the stream.
 */
export function triggerSharedAttachmentDownload(ticket: string): void {
  const frame = document.createElement("iframe");
  frame.style.display = "none";
  frame.src = sharedDownloadUrl(ticket);
  document.body.appendChild(frame);
  setTimeout(() => {
    frame.remove();
  }, 60000);
}

/**
 * Session-token storage for a password-protected link.
 *
 * `sessionStorage`, not `localStorage`: the unlock should not outlive the browser tab. Keyed per
 * token so a recipient holding several links does not have one link's unlock leak into another —
 * the server binds the token to a single link anyway, but keeping them separate avoids sending a
 * useless token and getting a needless password prompt.
 */
const sessionKey = (token: string) => `cube.share.session.${token}`;

/**
 * In-memory fallback for browsers whose `sessionStorage` THROWS on access (Safari private mode).
 * Every access below sits inside a `try`, so a store that is missing entirely lands in the same
 * catch as one that throws.
 *
 * Without it, unlocking a password-protected link there looped forever: the unlock succeeded, the
 * store silently failed, the viewer route re-resolved with no session token, got `requiresPassword`,
 * and sent the recipient back to the gate — each round also burning a lockout attempt. Memory does
 * not survive a reload (the recipient re-enters the password then), but it survives the navigation
 * from gate to viewer, which is the loop that mattered.
 */
const inMemorySessions = new Map<string, string>();

export function readStoredSession(token: string): string | undefined {
  try {
    return globalThis.sessionStorage.getItem(sessionKey(token)) ?? inMemorySessions.get(token);
  } catch {
    return inMemorySessions.get(token);
  }
}

export function storeSession(token: string, sessionToken: string): void {
  // Kept in BOTH places unconditionally: the map is what rescues a throwing sessionStorage, and
  // writing it first means even a partially-broken storage layer cannot lose the unlock.
  inMemorySessions.set(token, sessionToken);
  try {
    globalThis.sessionStorage.setItem(sessionKey(token), sessionToken);
  } catch {
    // Non-fatal — the in-memory copy above already has it.
  }
}

export function clearStoredSession(token: string): void {
  inMemorySessions.delete(token);
  try {
    globalThis.sessionStorage.removeItem(sessionKey(token));
  } catch {
    // Non-fatal.
  }
}

/**
 * Mint an INLINE view ticket for one file version and return the ticketed GET URL.
 *
 * The byte source for every share-mode sub-viewer (PDF, image/panorama, video, html): these all hold
 * a VERSION id, so the mint uses `fileVersionId` addressing and the server resolves + containment-
 * checks the owning file. Inline tickets are permitted on Can View links — that tier means "preview
 * in the browser only" — and carry a long TTL because video seeking and slow readers re-request the
 * same URL throughout a session (the server re-checks link liveness on every request regardless).
 *
 * Returns null when the link cannot serve these bytes (revoked mid-session, version vanished): the
 * caller decides between an error state and the dead-link screen. NEVER falls back to an
 * authenticated URL — that is the bug class this function exists to end.
 */
export async function mintSharedViewUrl(
  session: ShareViewerSessionLike,
  fileVersionId: string,
  options?: {
    /**
     * Mint against the version's upload-time CONVERSION ARTIFACT (the PDF a PPTX becomes) instead
     * of its source bytes — the share twin of the internal `viewFiles?type=cachedKeyName` suffix.
     */
    artifact?: boolean;
  },
): Promise<string | null> {
  const res = await requestSharedDownload({
    token: session.token,
    sessionToken: session.sessionToken,
    fileVersionId: asObjectIdString(fileVersionId),
    disposition: "inline",
    ...(options?.artifact ? { artifact: true } : {}),
  });
  // The recipient's 12h password session expired mid-view (or the password was rotated). Every
  // consumer of this function is a byte path deep inside a viewer with no way to re-prompt — so the
  // ONE correct recovery is a hard navigation back to the share entry, whose gate owns re-unlocking.
  // Without this, expiry looked like: a dead video player, a spinning image pane, a PDF "error"
  // whose retry can never succeed. A hard navigation (not SPA) also tears down every viewer timer.
  if (res.status && res.valid && res.requiresPassword) {
    clearStoredSession(session.token);
    globalThis.location.assign(`/share/${session.token}`);
    return null;
  }
  if (!res.status || !res.ticket) return null;
  return sharedDownloadUrl(res.ticket);
}

/** The two fields the mint needs — structurally satisfied by `ShareViewerSession`. */
type ShareViewerSessionLike = { token: string; sessionToken?: string | undefined };

/**
 * Validate a viewer-held version id into the branded id the request schema demands.
 *
 * Throwing on a malformed id (rather than casting) keeps the invariant that only real ObjectIds ever
 * reach the public routes; viewer code only ever holds ids the server itself issued, so a throw here
 * means memory corruption or a coding error, not user input.
 */
function asObjectIdString(id: string): RequestSharedDownloadRequest["fileVersionId"] & string {
  const parsed = objectIdString.safeParse(id);
  if (!parsed.success) throw new Error(`mintSharedViewUrl: not a version id: ${id}`);
  return parsed.data;
}

/**
 * File-version payload for the recipient's DesignViewer — the same shape
 * `/fileOperations/getFileVersionData` returns, so DesignViewer's engine routing works unchanged.
 */
/**
 * WOPI (Collabora) mint for a shared office file — the recipient twin of `/wopi/officeUrl`.
 * The returned token is ALWAYS read-only and bound to this one version; the caller builds the
 * WOPISrc + form-POST exactly like the internal OfficeViewer path.
 */
export async function getSharedOfficeUrl(params: {
  token: string;
  sessionToken?: string;
  fileVersionId: string;
  postMessageOrigin?: string;
}) {
  const res = await shareClient.post<GetSharedOfficeUrlResponse>(`${base()}/getSharedOfficeUrl`, {
    token: params.token,
    sessionToken: params.sessionToken,
    fileVersionId: asObjectIdString(params.fileVersionId),
    postMessageOrigin: params.postMessageOrigin,
  });
  return res.data;
}

export async function getSharedFileVersionData(params: GetSharedFileVersionDataRequest) {
  const res = await shareClient.post<GetSharedFileVersionDataResponse>(`${base()}/getSharedFileVersionData`, params);
  return res.data;
}

/**
 * Pre-authorised geometry URLs for a shared model — one per SCS asset, including a merged model's
 * constituents. Returns an empty list while conversion is still running.
 */
export async function getSharedModelAssets(params: GetSharedModelAssetsRequest) {
  const res = await shareClient.post<GetSharedModelAssetsResponse>(`${base()}/getSharedModelAssets`, params);
  return res.data;
}

/**
 * Share-scoped equivalent of `/folders/isFile403`, in the same response shape so the viewer's existing
 * permission gates work unchanged for a recipient.
 */
export async function getSharedFileAccess(params: GetSharedFileAccessRequest) {
  const res = await shareClient.post<GetSharedFileAccessResponse>(`${base()}/getSharedFileAccess`, params);
  return res.data;
}
